import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  Calendar,
  BookOpen,
  Settings,
  Image,
  Users,
  Monitor,
  MessageCircleQuestion,
  Wallet,
  Map,
  PawPrint,
  Lock,
  Home,
  Heart,
  TrendingUp,
  CheckCircle2,
  Circle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { useBusinessSetup } from '@/contexts/BusinessSetupContext';
import { useFeatures } from '@/contexts/FeaturesContext';
import { useTranslation } from 'react-i18next';
import { config } from '@/config';

interface MenuItem {
  key: string;
  icon: React.ElementType;
  label: string;
  path: string;
  always?: boolean;
  description?: string;
  badge?: string;
}

interface ProfileCheck {
  done: boolean;
  label: string;
  path: string;
}

export function BusinessSidebar() {
  const location = useLocation();
  const { unlockedSections, isLoading } = useBusinessSetup();
  const { t } = useTranslation('business', { keyPrefix: 'main' });
  const features = useFeatures();

  const [checks, setChecks] = useState<ProfileCheck[]>([]);

  useEffect(() => {
    const token = (() => {
      try {
        const ba = JSON.parse(localStorage.getItem('businessAuth') || '{}');
        const pa = JSON.parse(localStorage.getItem('auth') || '{}');
        return (ba.token ? ba : pa).token || '';
      } catch { return ''; }
    })();
    if (!token) return;

    fetch(`${config.apiUrl}/business/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.data) return;
        const biz = d.data;
        const mode = biz.contact?.bookingMode;
        const url  = biz.contact?.bookingUrl;
        const desc = biz.description || '';

        setChecks([
          {
            done:  !!biz.images?.logo,
            label: 'Logo uploaded',
            path:  '/business/settings',
          },
          {
            done:  !!biz.images?.cover,
            label: 'Cover photo',
            path:  '/business/settings',
          },
          {
            done:  desc.length > 20 && !desc.toLowerCase().includes('bla'),
            label: 'Description written',
            path:  '/business/settings',
          },
          {
            done:  mode === 'redirect' && !!url,
            label: 'Booking link set',
            path:  '/business/settings',
          },
        ]);
      })
      .catch(() => {});
  }, []);

  // Only include sidebar items whose feature flag is enabled.
  // always:true items (home, settings, partner-stats) are never hidden.
  const menuItems: MenuItem[] = [
    { key: 'home',             icon: Home,                 label: t('home'),          path: '/business',                always: true,                           description: t('returnToDashboard') },
    { key: 'analytics',        icon: Monitor,              label: t('analytics'),     path: '/business/analytics',      always: features.businessAnalytics,     description: t('viewBusinessMetrics') },
    { key: 'activities',       icon: Image,                label: t('activities'),    path: '/business/activities',     always: true,                           description: t('manageWildlifeExperiences') },
    { key: 'bookings',         icon: BookOpen,             label: t('bookings'),      path: '/business/bookings',       always: features.businessBookings,      description: t('handleReservations') },
    { key: 'custom-adventures',icon: Heart,                label: 'Custom Adventures',path: '/business/custom-adventures', always: features.customAdventures,   description: 'Manage custom adventures' },
    { key: 'calendar',         icon: Calendar,             label: t('calendar'),      path: '/business/calendar',       always: features.businessCalendar,      description: t('scheduleManageEvents') },
    { key: 'communication',    icon: MessageCircleQuestion,label: t('communication'), path: '/business/communication',  always: features.businessCommunication, description: t('manageCustomerInteractions') },
    { key: 'staff',            icon: Users,                label: t('staff'),         path: '/business/staff',          always: features.businessStaff,         description: t('manageTeamMembers') },
    { key: 'mapping',          icon: Map,                  label: t('mapping'),       path: '/business/mapping',        always: features.businessMapping,       description: t('createManageLocationMaps') },
    { key: 'livestock',        icon: PawPrint,             label: t('livestock'),     path: '/business/livestock',      always: features.businessLivestock,     description: t('manageWildlifeInventory') },
    { key: 'funding',          icon: Wallet,               label: t('funding'),       path: '/business/funding',        always: features.businessFunding,       description: t('accessFinancialResources') },
    { key: 'partner-stats',    icon: TrendingUp,           label: 'Partner Stats',    path: '/business/partner-stats',  always: true,                           description: 'Leads and traffic generated by Wildy' },
    { key: 'settings',         icon: Settings,             label: t('settings'),      path: '/business/settings',       always: true,                           description: t('configureBusinessSettings') },
  ].filter(item => item.always !== false); // drop items explicitly set to always:false
  
  if (isLoading && Object.keys(unlockedSections).length === 0) {
    return (
      <div className="hidden sm:block fixed left-0 w-64 h-screen bg-gradient-to-b from-white to-slate-50/50 border-r border-slate-200/60 px-4 py-6 shadow-lg shadow-slate-900/5">
        <div className="animate-pulse space-y-4 pt-20">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-12 bg-slate-100 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="hidden sm:flex fixed left-0 w-64 h-screen bg-gradient-to-b from-white via-slate-50/30 to-white border-r border-slate-200/60 px-4 shadow-lg shadow-slate-900/5 flex-col">
      {/* Main Navigation - grouped at top with space at bottom */}
      <nav className="flex-1 pt-20 pb-4 flex flex-col">
        {/* Navigation Items - Grouped Together */}
        <div className="space-y-4">
          {/* Primary Navigation */}
          <div className="space-y-2">
            {menuItems.slice(0, 5).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isUnlocked = item.always || unlockedSections[item.key];

              const menuItem = (
                <div
                  key={item.path}
                  className={cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-300 group",
                    isActive 
                      ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25 scale-[1.02]" 
                      : isUnlocked
                        ? "text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 hover:shadow-md hover:shadow-slate-900/5"
                        : "text-slate-300 cursor-not-allowed opacity-60"
                  )}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {isUnlocked ? (
                      <Icon className={cn(
                        "h-5 w-5 transition-all duration-300",
                        isActive ? "text-white" : "text-slate-500 group-hover:text-emerald-600"
                      )} />
                    ) : (
                      <Lock className="h-5 w-5" />
                    )}
                    <span className="font-medium text-sm">{item.label}</span>
                  </div>
                  {item.badge && isUnlocked && (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200/50 shadow-sm text-xs">
                      {item.badge}
                    </Badge>
                  )}
                </div>
              );

              return isUnlocked ? (
                <Link key={item.path} to={item.path}>
                  {menuItem}
                </Link>
              ) : (
                <div key={item.path}>
                  {menuItem}
                </div>
              );
            })}
          </div>

          {/* Business Features */}
          <div className="space-y-2">
            {menuItems.slice(5, -1).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isUnlocked = item.always || unlockedSections[item.key];

              const menuItem = (
                <div
                  key={item.path}
                  className={cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-300 group",
                    isActive 
                      ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25 scale-[1.02]" 
                      : isUnlocked
                        ? "text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 hover:shadow-md hover:shadow-slate-900/5"
                        : "text-slate-300 cursor-not-allowed opacity-60"
                  )}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {isUnlocked ? (
                      <Icon className={cn(
                        "h-5 w-5 transition-all duration-300",
                        isActive ? "text-white" : "text-slate-500 group-hover:text-emerald-600"
                      )} />
                    ) : (
                      <Lock className="h-5 w-5" />
                    )}
                    <span className="font-medium text-sm">{item.label}</span>
                  </div>
                  {item.badge && isUnlocked && (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200/50 shadow-sm text-xs">
                      {item.badge}
                    </Badge>
                  )}
                </div>
              );

              return isUnlocked ? (
                <Link key={item.path} to={item.path}>
                  {menuItem}
                </Link>
              ) : (
                <div key={item.path}>
                  {menuItem}
                </div>
              );
            })}
          </div>

        </div>

        <div className="flex-1"></div>
      </nav>

      {/* Setup checklist — computed from real profile data */}
      {checks.length > 0 && (
        <div className="border-t border-slate-200/60 bg-gradient-to-r from-slate-50/50 to-white p-4">
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
              {checks.every(c => c.done) ? 'Profile complete ✓' : 'Complete your profile'}
            </p>
            {checks.map(c => (
              <Link key={c.label} to={c.path}
                className={cn(
                  'flex items-center gap-2 text-xs rounded-lg px-2 py-1.5 transition-colors',
                  c.done
                    ? 'text-slate-400 hover:text-slate-600'
                    : 'text-amber-700 bg-amber-50 hover:bg-amber-100 font-medium'
                )}>
                {c.done
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  : <Circle className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
} 